/**
 * @fileoverview Connect caller-owned tab selection to shared workspace composition. AppTab children supply content and labels; action tabs invoke commands without becoming selected content. `useUrlTabState.js` can connect selection to deep links. This component owns presentation rather than navigation persistence or business actions.
 */

import React, { Children } from 'react'
import './AppTabView.css'

/**
 * The parent controls activeIndex. onTabChange receives {index}; action tabs call their onClick without becoming the selected panel. Use useUrlTabState when selection must be reflected in navigation.
 */
function AppTabView({ activeIndex, onTabChange, children, className, header, variant = 'default' }) {
    const tabs = Children.toArray(children).filter(Boolean)
    const activeTab = tabs[activeIndex]?.props?.action
        ? tabs.find(tab => !tab.props?.action) || null
        : tabs[activeIndex] || tabs.find(tab => !tab.props?.action) || null
    const variantClass = variant && variant !== 'default' ? `app-tab-view--${variant}` : null
    const rootClassName = [
        'app-tab-view',
        variantClass,
        className,
    ].filter(Boolean).join(' ')

    return (
        <div className={rootClassName}>
            {header}
            <ul className="app-tab-view__tablist" role="tablist">
                {tabs.map((tab, index) => (
                    <li
                        key={index}
                        role="presentation"
                        className={`app-tab-view__tabitem${tab.props.alignRight ? ' app-tab-view__tabitem--align-right' : ''}`}
                    >
                        <button
                            type="button"
                            role={tab.props.action ? undefined : 'tab'}
                            aria-label={tab.props.ariaLabel}
                            aria-selected={tab.props.action ? undefined : index === activeIndex}
                            className={`app-tab-view__tab${index === activeIndex && !tab.props.action ? ' is-active' : ''}${tab.props.action ? ' app-tab-view__tab--action' : ''}${tab.props.tabClassName ? ` ${tab.props.tabClassName}` : ''}`}
                            onClick={() => {
                                if (typeof tab.props.onClick === 'function') {
                                    tab.props.onClick()
                                }
                                if (!tab.props.action && typeof onTabChange === 'function') {
                                    onTabChange({ index })
                                }
                            }}
                        >
                            {tab.props.header}
                        </button>
                    </li>
                ))}
            </ul>
            <div role="tabpanel" className="app-tab-view__panel">
                {activeTab}
            </div>
        </div>
    )
}



function AppTab({ header, children }) {
    return <div>{children}</div>
}

export { AppTabView, AppTab }
